import { AxiosError } from 'axios'
import {
  FunctionComponent, useCallback, useEffect, useRef, useState
} from 'react'
import { useSelector } from 'react-redux'
import api from '../../../../api'
import { IFolder, ListSummary } from '../../../../api/FolderApi'
import ErrorView from '../../../ErrorView'
import LoadSpinner from '../../../loadSpinner/LoadSpinner'
import Folder, { FolderMode } from './Folder'
import { IAlphaprocessState } from '../../../../store'

interface Props {
  onSelect: (item: any) => void
  selectedId?: string | number
  checklistStatus: 'open' | 'closed' | 'due' | 'invited'
  mode: FolderMode
  onUpdateCurrentFolder?: (folderId: string) => void
  currentFolderId?: string
  tabIndex: number
  // new design: counts of the list (e.g. "Offen 23" in the tab)
  onSummary?: (summary: ListSummary | null) => void
}

const FolderWrapper: FunctionComponent<Props> = ({
  currentFolderId, onUpdateCurrentFolder, mode, onSelect, selectedId, tabIndex, checklistStatus, onSummary
}) => {
  const [currentFolder, setCurrentFolder] = useState<IFolder | null>()
  const [error, setError] = useState<AxiosError | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  // Tracks the latest fetch so stale in-flight requests don't overwrite newer state
  const fetchIdRef = useRef(0)

  const fetchFolder = useCallback(async () => {
    const fetchId = ++fetchIdRef.current
    setIsLoading(true)
    setError(null)
    try {
      const rootFolder = await api.folders.root(tabIndex)
      if (fetchId !== fetchIdRef.current) return  // superseded by a newer fetch

      let folder = rootFolder
      if (currentFolderId?.length) {
        folder = await api.folders.find(currentFolderId, tabIndex)
      }

      if (fetchId !== fetchIdRef.current) return  // superseded by a newer fetch
      setCurrentFolder(folder)
    } catch (e) {
      if (fetchId !== fetchIdRef.current) return
      setError(e as AxiosError)
    } finally {
      if (fetchId === fetchIdRef.current) {
        setIsLoading(false)
      }
    }
  }, [currentFolderId, tabIndex])

  const handleUpdateFolder = useCallback(async (id: string) => {
    const isSameFolder = id === currentFolder?.id
    if (!isSameFolder) {
      // Cancel any in-flight fetch immediately — before the useEffect even fires.
      // This closes the gap where F_initial (currentFolderId=null) could finish
      // after onUpdateCurrentFolder but before fetchFolder increments fetchIdRef.
      ++fetchIdRef.current
      setIsLoading(true)  // show spinner immediately, prevents flash of old content
    }
    onUpdateCurrentFolder(id)
    if (isSameFolder) {
      // Same-folder refresh (e.g. after create/delete/move): currentFolderId won't
      // change so the useEffect won't re-fire; call fetchFolder explicitly.
      await fetchFolder()
    }
    // For a different folder, onUpdateCurrentFolder changes currentFolderId,
    // which recreates fetchFolder, which triggers the useEffect below.
  }, [currentFolder, fetchFolder, onUpdateCurrentFolder])

  const handleSelect = useCallback(async ({ type, item }) => {
    if (type === 'checklist') {
      onSelect(item)
    } else if (type === 'folder') {
      await handleUpdateFolder(item.id)
    }
  }, [handleUpdateFolder, onSelect])

  useEffect(() => {
    fetchFolder()
  }, [fetchFolder])

  // offline the list shows what is on the device: back online, load the real one again
  const isOffline = useSelector((state: IAlphaprocessState) => !!state.core.isOffline)
  const wasOffline = useRef(isOffline)
  useEffect(() => {
    if (wasOffline.current && !isOffline) fetchFolder()
    wasOffline.current = isOffline
  }, [isOffline, fetchFolder])

  if (isLoading) {
    return <LoadSpinner className="centered" />
  }
  return (
    <div className="folder-wrapper">
      {error && <ErrorView error={error} onRetry={fetchFolder} />}
      {currentFolder && (
        <Folder
          onError={setError}
          onSelect={handleSelect}
          folder={currentFolder}
          mode={mode}
          selectedId={selectedId}
          onUpdateFolder={handleUpdateFolder}
          checklistStatus={checklistStatus}
          tabIndex={tabIndex}
          onSummary={onSummary}
        />
      )}
    </div>
  )
}

export default FolderWrapper
